import { CameraAnimationProps } from "@/components/r3f/animations/camera-animation";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { IElementOrthomosaic } from "@/types/ielement-extra-types";
import {
  getSheetSize,
  IElementsState,
  parseVector3,
  selectIElementWorldTransform,
  Z_TO_Y_UP_QUAT,
} from "@faro-lotv/app-component-toolbox";
import { DEFAULT_PERSP_FOV, DEG_TO_RAD } from "@faro-lotv/lotv";
import { createSelector } from "@reduxjs/toolkit";
import { Camera, Vector3 } from "three";
import {
  ANIMATION_DURATION,
  FlyWalkSceneActiveElement,
} from "../flywalk-types";
import {
  FlyWalkAnimation,
  FlyWalkRenderingAnimation,
} from "./flywalk-animation";
import { runToImgAnimation } from "./flywalk-to-pano-animation";

/**
 * Computes a suitable fly walk animation to transition the camera to an Orthomosaic, and sets it via the given function
 *
 * @param main The Orthomosaic that is target for the animation
 * @param previousMain The iElements that is rendered at the start of the animation
 * @param renderingAnimation The rendering animation for flywalk mode. Rendering
 * animation parameters are also edited by this function.
 * @param camera The camera used to render the scene
 * @param getState Function to get the current state of the app store
 * @param setFlyWalkAnimation Function to set the current flywalk animation
 * @param endAnimation Function to call when the animation is finished
 */
export function runToOrthomosaicAnimation(
  main: IElementOrthomosaic,
  previousMain: FlyWalkSceneActiveElement | null,
  renderingAnimation: FlyWalkRenderingAnimation,
  camera: Camera,
  getState: () => RootState,
  setFlyWalkAnimation: (animation: FlyWalkAnimation | null) => void,
): void {
  const cameraAnimation = selectAnimationToOrthomosaic(main)(getState());

  runToImgAnimation(
    cameraAnimation,
    camera,
    renderingAnimation,
    previousMain,
    setFlyWalkAnimation,
  );
}

const selectAnimationToOrthomosaic = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, orthomosaic: IElementOrthomosaic) =>
        orthomosaic,
    ],
    computeAnimationToOrthomosaic,
  ),
);

function computeAnimationToOrthomosaic(
  iElements: IElementsState,
  orthomosaic: IElementOrthomosaic,
): CameraAnimationProps {
  const pose = selectIElementWorldTransform(orthomosaic.id)({ iElements });

  const quaternion = Z_TO_Y_UP_QUAT.clone();
  const position = parseVector3(pose.position);
  const sheetSize = getSheetSize(orthomosaic);
  position.add(
    new Vector3(
      sheetSize.width * 0.5 * pose.scale[0],
      sheetSize.height * 0.5 * pose.scale[0],
      sheetSize.height * 0.5 * INV_TAN * pose.scale[0],
    ).applyQuaternion(quaternion),
  );

  return {
    position,
    quaternion,
    duration: ANIMATION_DURATION,
    fov: DEFAULT_PERSP_FOV,
  };
}

const INV_TAN = 1.0 / Math.tan(DEFAULT_PERSP_FOV * 0.5 * DEG_TO_RAD);
